import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container } from 'styles/website/profile';
import { TitleBar } from 'styles/layout/titlebar';
import WebsiteService from 'services/website';
import useTitle from 'hooks/useTitle';
import { Content } from 'styles/globalStyles';
import WPSDataTable from 'components/wpstaq/WPSDataTable/WPSDataTable';
import { useSelector } from 'react-redux';
import { timezoneSelector } from 'store/me/meSelectors';
import { useHistory } from 'react-router-dom';
import DialogHelper from 'helpers/dialog';
import JsxHelper from 'helpers/jsx';
import useOnClickOutside from 'utils/useOnClickOutside';
import useQuery from 'hooks/useQuery';
import { isEmptyOrNull, convertToFilterObject, handleFilterURLParamsChanges } from 'helpers';
import StringHelper from 'helpers/string';

const viewerDefaults = { title: '', isOpen: false, data: {} };
const ActivityLogs = ({ website }) => {
  useTitle('Website - Activity Logs');
  const SYSTEM_ACCOUNT = 'System';
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({ page: 1, limit: 100, website_slug: website.slug });
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [viewer, setViewer] = useState({ ...viewerDefaults });
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const query = useQuery();
  const mounted = useRef(true);
  const filtersRef = useRef();
  const timezone = useSelector(timezoneSelector);
  const history = useHistory();

  // ----------------
  // LIFECYCLE HOOKS
  // ----------------

  useEffect(() => {
    if (mounted.current) {
      fetchEventLogs();
    }
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleFilterURLParamsChanges(filters, history);
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    refreshFilters();
    // eslint-disable-next-line
  }, [logs]);

  // ----------------
  // FILTERS
  // ----------------

  useOnClickOutside(filtersRef, () => setShowFilters(false));

  const prepareLogsForFilters = (logs) => logs.map(log => ({
    ...log,
    user_email: log.user_email || SYSTEM_ACCOUNT, // Ensure user_email is set to SYSTEM_ACCOUNT if not available
    guid: StringHelper.randomSlug(16)
  }));

  const refreshFilters = () => {
    if (isEmptyOrNull(logs)) {
      return;
    }
    const action = logs.map(log => log.code);
    const status = logs.map(log => log.status);
    const account = logs.map(log => log.user_email || SYSTEM_ACCOUNT);
    setFilters(prev => ({
      ...prev,
      ...convertToFilterObject(query, 'Action', new Set(action)),
      ...convertToFilterObject(query, 'Status', new Set(status)),
      ...convertToFilterObject(query, 'Account', new Set(account)),
    }));
  };

  // ----------------
  // API CALLS
  // ----------------

  const fetchEventLogs = () => {
    setLoading(true);
    WebsiteService.fetchWebsiteEventLogs(params)
      .then(res => {
        if (mounted.current) {
          setLogs(prev => [...prev, ...prepareLogsForFilters(res)]);
          setParams(prev => ({ ...prev, page: prev.page + 1 }));
          setCanLoadMore(res.length >= params.limit);
        }
      })
      .finally(() => setLoading(false));
  }

  // ----------------
  // HANDLERS
  // ----------------

  const handleViewerClose = useCallback(() => setViewer({ ...viewerDefaults }), []);

  const handleCellClick = (_title, value) => setViewer({
    title: _title,
    isOpen: true,
    data: value || {},
  });

  const handleFilterClick = e => {
    const { id, name, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: prev[name].map(i => (i.value === id ? { ...i, is_checked: checked } : i)),
    }));
  };

  // ----------------
  // TABLE DATA
  // ----------------

  const headers = [
    JsxHelper.createTableTimeHeader('created_at', timezone, '12%'),
    JsxHelper.createTableUserHeader('16%', 'Account', SYSTEM_ACCOUNT, null, 'user_email'),
    JsxHelper.createTableTextHeaderWithCallback('code', 'Action', '19%', row => row.code),
    JsxHelper.createTableStatusHeader('status', null, true, '8%'),
    JsxHelper.createTableTextHeaderWithCallback('payload', 'Payload', '7%', row => JsxHelper.createIcon({
      icon: 'expand',
      color: 'info',
      style: { cursor: 'pointer', marginLeft: '15px' },
      onClick: () => handleCellClick('Payload', row.data || {}),
    }), false),
    JsxHelper.createTableTextHeaderWithCallback('timeline', 'Messages', '38%', row => <div className='cell-wrapper' style={{marginRight: '5px'}}>
      {isEmptyOrNull(row.timeline)
        ? <p className='font-small font-italic'>No messages available</p>
        : JsxHelper.createEventLogTable(row.timeline || [], timezone, 'created_at')
      }
    </div>, false)
  ];

  // ----------------
  // RENDER
  // ----------------

  return (
    <Container className='margin-0'>
      <TitleBar className='titlebar'>
        <TitleBar.Title>Activity History</TitleBar.Title>
        <TitleBar.Actions>
          {JsxHelper.createFilterIcon({
            setShowFilters,
            filters,
            showFilters,
            onClickFilter: handleFilterClick,
            filtersRef,
            style: { marginLeft: '0' },
          })}
          {JsxHelper.createButton({
            label: 'Load More',
            onClick: () => fetchEventLogs(),
            disabled: !canLoadMore,
            loading,
          })}
        </TitleBar.Actions>
      </TitleBar>
      <p className='color-primary subheader'>
        This page shows the activity history of your website. It includes actions performed by the system, such as updates, changes, and other significant events. You can click on the "Payload" to view detailed information about each action.
      </p>
      <Content>
        <WPSDataTable columns={headers} body={logs} loading={loading} dataKey='guid'/>
      </Content>
      {viewer.isOpen && DialogHelper.jsonViewer({
        onClose: handleViewerClose,
        data: viewer.data,
      })}
    </Container>
  );
};

export default ActivityLogs;
